import { useMemo } from 'react'

import {
  formatDate,
  matchFilterArrayIncludes,
  matchFilterBetweenDates,
  matchFilterNumber,
  matchFilterString,
  sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import { mapCampaignStatuses, matchFilterCampaignStatuses } from '@functions/campaignStatus'

import useSelector from '@hooks/useSelector'
import useReduxAction from '@hooks/useReduxAction'

import type { CampaignModel } from '@models/types'

import { sortByPriorityCampaigns } from '@functions/priorityCampaign'

const watchEntityKeys = ['campaigns', 'campaignStatuses']

interface CampaignFilters {
  agency_id?: number,
  agency_marketing?: 'true' | 'false',
  agent_id?: number,
  auction_date_start?: string,
  campaign_status?: string,
  hidden?: 'true' | 'false',
  limit?: number,
  sort_by_priority_campaign_for_user_id?: number,
  priority_campaign_user_id?: number[],
  published?: 'all' | 'true' | 'false',
  order_by?: `${string} ${'asc' | 'desc'}`,
  string?: string,
}

interface Options {
  filters?: CampaignFilters,
  performHttpRequests?: boolean,
  requestOptions?: {
    [key: string]: boolean,
  },
  sortBy?: string,
}

function useCampaigns(options: Options = {}) {
  const { filters, requestOptions, sortBy = 'id desc' } = options

  const {
    agency_id: filterAgencyId,
    agency_marketing: filterAgencyMarketing,
    agent_id: filterAgentId,
    auction_date_start: filterAuctionDateStart,
    campaign_status: filterCampaignStatus,
    hidden: filterHidden,
    limit: filterLimit,
    priority_campaign_user_id: filterPriorityCampaignsByUserId,
    published: filterPublished,
    sort_by_priority_campaign_for_user_id: sortByPriorityCampaignUserId,
    string,
  } = filters || {}

  const {
    updatedEntities: {
      campaigns: campaignsUpdatedAt,
      campaignStatuses: campaignStatusesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaigns, campaignStatuses } = useSelector(reduxState => reduxState.entities)

  // Using the mapped statuses it is easy to get the ID without looping
  // mappedCampaignStatuses['withdrawn'] => 1 etc
  const mappedCampaignStatuses = mapCampaignStatuses(campaignStatuses)
  // @ts-expect-error TS2538
  const campaignStatusId = mappedCampaignStatuses[filterCampaignStatus]

  const filteredCampaigns = useMemo((): CampaignModel[] => {
    const filtered = Object.values(campaigns).filter((campaign) => {
      const {
        agency_id,
        agent_ids = [],
        agency_marketing,
        dates, hidden,
        id,
        priority_campaign_user_ids = [],
        published,
        title,
      } = campaign

      const auctionDate = formatDate(dates?.auction?.date, 'dd/MM/yyyy')

      const matchCampaignStatus = filterCampaignStatus
        ? matchFilterCampaignStatuses({ campaign, campaignStatusId, campaignStatusShortName: filterCampaignStatus })
        : true

      const matchAgency = matchFilterNumber(agency_id, filterAgencyId)
      const matchAgents = filterAgentId ? matchFilterArrayIncludes(agent_ids, Number(filterAgentId)) : true

      const matchHidden = filterHidden ? hidden.toString() === filterHidden : true

      const matchPublished = matchFilterString(published?.toString(), filterPublished, { exact: true })

      const matchAgencyMarketing = filterAgencyMarketing ? agency_marketing?.toString() === filterAgencyMarketing : true

      const matchAuctionDate = matchFilterBetweenDates(auctionDate, filterAuctionDateStart)

      const matchPriorityCampaigns = filterPriorityCampaignsByUserId
        ? matchFilterArrayIncludes(priority_campaign_user_ids, Number(filterPriorityCampaignsByUserId)) : true

      // String match (ID & Title)
      const matchId = string ? id.toString() === string : true
      const matchTitle = matchFilterString(title, string)
      const matchString = matchId || matchTitle

      return matchAgency
        && matchAgencyMarketing
        && matchAgents
        && matchAuctionDate
        && matchCampaignStatus
        && matchHidden
        && matchPriorityCampaigns
        && matchPublished
        && matchString
    })

    const [column, direction] = sortBy.split(' ')
    return sortByPriorityCampaignUserId
      ? sortByPriorityCampaigns({
        entities: filtered,
        selectedPriorityUserId: sortByPriorityCampaignUserId,
      }) as CampaignModel[]
      : sortArrayBy(filtered, direction as 'asc' | 'desc', column as keyof CampaignModel)
  }, [campaignsUpdatedAt, campaignStatusesUpdatedAt, JSON.stringify(options)])

  const filteredCampaignsCount = filteredCampaigns.length
  const hasFilteredCampaigns = !!filteredCampaigns.length

  const loadMorePayload = useLoadMore({
    filters,
    limit: filterLimit,
    loadedCount: filteredCampaignsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaigns } = useReduxAction(
    'campaigns',
    'loadCampaigns',
    {
      ...filtersWithOffset,
      ...requestOptions,
      limit,
    },
    [performHttpRequests, filtersWithOffset],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaigns,
    filteredCampaignsCount,
    hasFilteredCampaigns,
    loading: loadingCampaigns,
  }
}

export type UseCampaignsPayload = ReturnType<typeof useCampaigns>

export default useCampaigns
